import { Link } from "react-router-dom";
import { Smartphone, Tablet, Laptop, Watch, ShieldCheck, Clock, Sparkles, ArrowRight, Wrench } from "lucide-react";

const devices = [
  { id: "phone", label: "Phones", icon: Smartphone, testid: "device-card-phone", tint: "from-amber-50 to-white" },
  { id: "laptop", label: "Laptops", icon: Laptop, testid: "device-card-laptop", tint: "from-sky-50 to-white" },
  { id: "tablet", label: "Tablets", icon: Tablet, testid: "device-card-tablet", tint: "from-teal-50 to-white" },
  { id: "watch", label: "Smartwatches", icon: Watch, testid: "device-card-watch", tint: "from-rose-50 to-white" },
];

const trust = [
  { icon: ShieldCheck, label: "Quality Replacement Parts", desc: "Carefully sourced and selected for dependable repairs" },
  { icon: Clock, label: "Same-day drop-off", desc: "Pick a slot online, walk right in" },
  { icon: Sparkles, label: "Transparent estimates", desc: "Parts + labor shown separately, no surprises" },
];

const steps = [
  { n: "01", title: "Pick Your Device", desc: "Choose your phone, tablet, laptop, or smartwatch." },
  { n: "02", title: "Tell Us What's Wrong", desc: "Select the problems you're experiencing and tell us about your device." },
  { n: "03", title: "Get Your Instant Estimate", desc: "See an upfront preliminary estimate with parts and labor shown separately." },
  { n: "04", title: "Schedule Your Drop-Off", desc: "Choose a convenient day and time to bring us your device." },
  { n: "05", title: "Inspection & Authorization", desc: "We inspect your device and provide the final written repair estimate for your approval before repair begins." },
  { n: "06", title: "Repair, Track & Pickup", desc: "Once authorized, we'll repair your device, keep you updated, and let you know when it's ready." },
];

export default function Home() {
  return (
    <div className="pt-16">
      {/* Hero */}
      <section className="relative overflow-hidden hero-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 md:pt-24 md:pb-32 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 fade-up">
            <div className="overline mb-4">The Repair Nook · Bench-tested since day one</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Precision repair for the devices you <span className="text-amber-600">actually rely on.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              Get a preliminary repair estimate in under a minute — with parts and
              labor shown separately. Final pricing is confirmed after in-person
              inspection.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="#services" data-testid="hero-cta-book-now" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors shadow-sm">
                Get your instant repair estimate <ArrowRight className="w-4 h-4" /></a>
              <Link
                to="/track"
                data-testid="hero-cta-track"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-slate-900 font-semibold border border-slate-200 hover:border-slate-400 transition-colors"
              >
                Track my repair
              </Link>
            </div>
            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              {trust.map((t) => (
                <div key={t.label} className="p-4 rounded-xl bg-white border border-slate-200/80">
                  <t.icon className="w-5 h-5 text-amber-600 mb-2" />
                  <div className="text-sm font-semibold text-slate-900">{t.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{t.desc}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border border-slate-200/80 shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1550041473-d296a3a8a18a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxzbWFydHBob25lJTIwcmVwYWlyJTIwdGVjaG5pY2lhbiUyMGZpeCUyMHNjcmVlbnxlbnwwfHx8fDE3ODg0NjI4NTR8MA&ixlib=rb-4.1.0&q=85"
                alt="Repair technician at workbench"
                className="w-full h-[520px] object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md rounded-2xl p-4 border border-slate-200/80 shadow-sm">
                <div className="flex items-center gap-3">
                  <Wrench className="w-5 h-5 text-amber-600" />
                  <div>
                    <div className="text-xs overline">Live bench</div>
                    <div className="text-sm font-semibold text-slate-900">
                      Average turnaround: 2 business days
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="overline mb-3">Step 1</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              Pick the device we're fixing today.
            </h2>
            <p className="mt-3 text-slate-600">
              Tap a category to open the configurator. You'll pick the specifics next.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {devices.map((d) => (
              <Link
                key={d.id}
                to={`/services/${d.id}`}
                data-testid={d.testid}
                className={`group p-6 md:p-8 rounded-2xl border border-slate-200 bg-gradient-to-br ${d.tint} hover:border-slate-900 hover:shadow-md transition-all`}
              >
                <div className="w-12 h-12 rounded-xl bg-slate-900 text-amber-400 flex items-center justify-center group-hover:rotate-6 transition-transform">
                  <d.icon className="w-6 h-6" />
                </div>
                <div className="mt-6 font-heading font-bold text-xl text-slate-900">{d.label}</div>
                <div className="mt-2 text-sm text-slate-500 flex items-center gap-1">
                  Start quote <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="overline mb-3">How it works</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              Six clear steps. No guessing games.
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {steps.map((s) => (
              <div
                key={s.n}
                data-testid={`step-${s.n}`}
                className="p-6 md:p-8 rounded-2xl border border-slate-200/80 bg-white hover:shadow-md transition-shadow"
              >
                <div className="font-mono text-xs text-amber-600 font-bold tracking-widest">{s.n}</div>
                <div className="mt-3 font-heading font-semibold text-xl text-slate-900">{s.title}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing signal */}
      <section className="py-16 md:py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline mb-3" style={{ color: "#FBBF24" }}>Transparent pricing</div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              Parts up front. Labor at pickup. Never a hidden line item.
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              We split every bid so you can see exactly what you're paying for. You pay
              for parts online when you book — that's what we order from our supplier.
              Labor is due when you come pick up your device.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-2xl bg-amber-50/10 border border-amber-500/30 p-6">
              <div className="overline" style={{ color: "#FBBF24" }}>Parts</div>
              <div className="mt-2 font-mono text-3xl font-bold">Paid online</div>
              <div className="mt-2 text-sm text-slate-300">Stripe checkout when you book.</div>
            </div>
            <div className="rounded-2xl bg-sky-50/10 border border-sky-400/30 p-6">
              <div className="overline" style={{ color: "#7DD3FC" }}>Labor</div>
              <div className="mt-2 font-mono text-3xl font-bold">Paid at pickup</div>
              <div className="mt-2 text-sm text-slate-300">Cash or card in-store.</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
